import React from "react";
import {
  BrainCircuit,
  Clock,
  BarChart2,
  Activity,
  Zap,
  ArrowRight,
  Sparkles,
  CalendarDays,
  Calendar,
  TrendingUp,
  TrendingDown,
  Banknote,
} from "lucide-react";
import { formatVND1000, formatVND } from '../../../../utils/format';
import { getPriorityStyle, getDriverValue } from '../../../../services/service';
import { renderStandardChart } from './StandardChart';

const getStatusInfo = (label) => {
  const safeLabel = label ? label.toUpperCase() : "";
  if (safeLabel.includes("KHOE") || safeLabel.includes("GOOD")) {
    return {
      color: "text-green-500",
      bg: "bg-green-500",
      icon: <TrendingUp size={18} />,
      text: "KHỎE MẠNH",
    };
  }
  if (safeLabel.includes("YEU") || safeLabel.includes("POOR")) {
    return {
      color: "text-red-500",
      bg: "bg-red-500",
      icon: <TrendingDown size={18} />,
      text: "YẾU / CẦN CẢI THIỆN",
    };
  }
  if (safeLabel.includes("NGUY_CAP")) {
    return {
      color: "text-red-500",
      bg: "bg-red-500",
      icon: <TrendingDown size={18} />,
      text: "NGUY CẤP",
    };
  }
  return {
    color: "text-yellow-500",
    bg: "bg-yellow-500",
    icon: <Activity size={18} />,
    text: "TRUNG BÌNH",
  };
};

const renderTrendLine = (forecastData) => {
  if (!forecastData || forecastData.length === 0) return null;

  const data = forecastData.map((d) => parseFloat(d.revenue_pred));
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;

  const first = data[0];
  const last = data[data.length - 1];
  const trend = last > first ? "up" : "down";
  const color = trend === "up" ? "#10b981" : "#ef4444";

  const points = data
    .map((val, idx) => {
      const x = (idx / (data.length - 1)) * 100;
      const y = 100 - ((val - min) / range) * 80 - 10;
      return `${x},${y}`;
    })
    .join(" ");

  return (
    <svg
      viewBox="0 0 100 100"
      className="w-full h-full overflow-visible"
      preserveAspectRatio="none"
    >
      <polyline
        fill="none"
        stroke={color}
        strokeWidth="3"
        points={points}
        vectorEffect="non-scaling-stroke"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <defs>
        <linearGradient id={`gradient-${trend}`} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.2" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon
        points={`0,100 ${points} 100,100`}
        fill={`url(#gradient-${trend})`}
      />
    </svg>
  );
};

export default function AIAnalysisSection({ aiAnalysis, revenueData }) {
  if (!aiAnalysis) return null;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 border-b border-slate-200 pb-2">
        <BrainCircuit className="text-purple-600" size={24} />
        <h2 className="text-xl font-bold text-slate-800">
          Báo Cáo AI & Dự Báo Dòng Tiền
        </h2>
      </div>

      <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200 flex flex-col md:flex-row justify-between items-center gap-6">
        <div className="flex md:flex-row flex-col items-center gap-5">
          <div className="h-16 px-4 rounded-2xl bg-blue-50 flex items-center justify-center items-center text-blue-700 font-black border border-blue-100 shadow-inner">
            <span className="text-3xl">{aiAnalysis.merchant_rank}</span> 
            <span className="text-4xl font-normal">/</span> 
            <span className="text-sm text-gray-600">{aiAnalysis.overall_ranking}</span>  
          </div>
          <div>
            <h2 className="text-3xl font-black text-slate-900 tracking-tight">
              {aiAnalysis.store_name || aiAnalysis.merchant_id}
            </h2>
            <div className="flex items-center gap-3 text-sm text-slate-500 mt-1">
              {/* <span className="font-bold text-slate-600 bg-slate-100 px-2.5 py-0.5 rounded-md border border-slate-200">
                {aiAnalysis.merchant_id}
              </span> */}
              <span className="flex items-center gap-1.5 font-medium">
                <Clock size={14} className="text-slate-400" /> Cập nhật:{" "}
                {new Date(aiAnalysis.created_at).toLocaleString("vi-VN")}
              </span>
            </div>
          </div>
        </div>
        {/* {(() => {
          const statusInfo = getStatusInfo(aiAnalysis.health_label);
          return (
            <div className="flex items-center gap-6 bg-slate-50 px-6 py-4 rounded-2xl border border-slate-200 shadow-sm w-full md:w-auto justify-center md:justify-end">
              <div className="text-right">
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">
                  Chỉ số Sức Khỏe
                </p>
                <p
                  className={`text-4xl font-black ${statusInfo.color} tracking-tighter`}
                >
                  {Math.round(aiAnalysis.health_score)}
                  <span className="text-xl font-bold text-slate-400">/100</span>
                </p>
              </div>
              <div className={`h-12 w-1.5 rounded-full ${statusInfo.bg}`}></div>
              <div className="text-left">
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">
                  Trạng thái
                </p>
                <div
                  className={`flex items-center gap-1.5 font-black ${statusInfo.color} text-lg whitespace-nowrap`}
                >
                  {statusInfo.icon}
                  {statusInfo.text}
                </div>
              </div>
            </div>
          );
        })()} */}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
        {/* CỘT 1: CHỈ SỐ DẪN DẮT */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden flex flex-col h-full lg:h-[450px]">
          <div className="p-5 border-b border-slate-100 flex items-center gap-2 bg-slate-50/50">
            <Activity className="text-blue-600" size={20} />
            <h3 className="font-bold text-slate-900 text-lg">
              Chỉ số dẫn dắt (Drivers)
            </h3>
          </div>
          <div className="p-6 flex flex-col gap-6">
            <div className="pb-5 border-b border-slate-100 border-dashed flex justify-between items-end">
              <div>
                {/* Đã bổ sung biến aiAnalysis.date từ data thật */}
                <p className="text-sm font-bold text-slate-500 mb-1">
                  Doanh thu gần nhất{" "}
                  {aiAnalysis.date ? (
                    <span className="text-blue-600 font-medium">
                      ({aiAnalysis.date})
                    </span>
                  ) : (
                    ""
                  )}
                </p>
                <span className="text-4xl font-black text-slate-900 tabular-nums tracking-tight">
                  {formatVND1000(
                    getDriverValue(aiAnalysis.drivers, "revenue_today"),
                  )}
                </span>
              </div>
              <div className={`flex flex-col items-end px-2`}>
                <span
                  className={`text-sm font-bold flex items-center gap-1 px-2.5 py-1 rounded-lg shadow-sm border ${parseFloat(getDriverValue(aiAnalysis.drivers, "vs_7d")) >= 0 ? "text-emerald-700 bg-emerald-50 border-emerald-200" : "text-red-700 bg-red-50 border-red-200"}`}
                >
                  {parseFloat(getDriverValue(aiAnalysis.drivers, "vs_7d")) >=
                  0 ? (
                    <TrendingUp size={16} />
                  ) : (
                    <TrendingDown size={16} />
                  )}{" "}
                  {getDriverValue(aiAnalysis.drivers, "vs_7d")}
                </span>
                <span className="text-[10px] font-bold text-slate-400 uppercase mt-1">
                  So TB 7 ngày
                </span>
              </div>
            </div>
            <div className="space-y-4">
              <div className="flex justify-between items-center text-sm">
                <span className="text-slate-600 font-semibold">
                  Trung bình 7 ngày
                </span>
                <span className="font-bold text-slate-900 tabular-nums">
                  {formatVND1000(
                    getDriverValue(aiAnalysis.drivers, "revenue_7d_avg"),
                  )}
                </span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span className="text-slate-600 font-semibold">
                  Tăng trưởng (30 ngày)
                </span>
                <span
                  className={`font-bold tabular-nums flex items-center gap-1 ${parseFloat(getDriverValue(aiAnalysis.drivers, "vs_30d")) >= 0 ? "text-emerald-600" : "text-red-600"}`}
                >
                  {parseFloat(getDriverValue(aiAnalysis.drivers, "vs_30d")) >=
                  0 ? (
                    <TrendingUp size={14} />
                  ) : (
                    <TrendingDown size={14} />
                  )}{" "}
                  {getDriverValue(aiAnalysis.drivers, "vs_30d")}
                </span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span className="text-slate-600 font-semibold">
                  Độ biến động
                </span>
                <span className="font-bold text-amber-700 bg-amber-50 px-2 py-0.5 rounded border border-amber-100">
                  {getDriverValue(aiAnalysis.drivers, "volatility_rel_7d")}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* CỘT 2: DỰ BÁO */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden flex flex-col h-full lg:h-[450px]">
          <div className="p-5 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
            <div className="flex items-center gap-2">
              <BarChart2 className="text-blue-600" size={20} />
              <h3 className="font-bold text-slate-900 text-lg">
                Dự báo 7 ngày tới
              </h3>
            </div>
            <span className="text-[10px] font-black text-blue-700 bg-blue-100 border border-blue-200 px-2 py-1 rounded uppercase tracking-wider">
              AI Model
            </span>
          </div>
          <div className="p-6 flex-1 flex flex-col">
            <div className="flex-1 w-full bg-[#F8FAFC] rounded-xl p-4 relative border border-slate-200 shadow-inner group">
              <div className="h-full w-full pt-6 pb-2">
                {renderStandardChart(
                  aiAnalysis.forecast_next_7d,
                  "revenue_pred",
                  "date",
                )}
              </div>
              <div className="absolute top-3 left-4">
                <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-0.5">
                  Dự kiến ngày mai
                </p>
                <p className="text-xl font-black text-slate-900 tabular-nums">
                  {formatVND1000(
                    aiAnalysis.forecast_next_7d?.[0]?.revenue_pred || 0,
                  )}
                </p>
              </div>
              <div className="absolute bottom-3 right-4 text-[10px] font-bold text-slate-400 bg-white/80 px-2 py-1 rounded border border-slate-200">
                Di chuột vào biểu đồ để xem
              </div>
            </div>
          </div>
        </div>

        {/* CỘT 3: HÀNH ĐỘNG (CÓ THANH CUỘN NẾU DÀI) */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden flex flex-col h-full lg:h-[450px]">
          <div className="p-5 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
            <div className="flex items-center gap-2">
              <Zap className="text-amber-500 fill-amber-500" size={20} />
              <h3 className="font-bold text-slate-900 text-lg">
                Đề xuất Hành động
              </h3>
            </div>
            <span className="bg-amber-100 text-amber-800 text-xs font-black px-2 py-0.5 rounded-full">
              {aiAnalysis.actions_today?.length || 0}
            </span>
          </div>
          <div className="p-5 flex-1 overflow-y-auto custom-scrollbar bg-slate-50/30">
            <div className="space-y-4">
              {aiAnalysis.actions_today?.length > 0 ? (
                aiAnalysis.actions_today.map((item, idx) => (
                  <div
                    key={idx}
                    className={`p-5 rounded-xl bg-white shadow-sm border border-slate-200 relative overflow-hidden group`}
                  >
                    <div
                      // className={`absolute top-0 left-0 w-1.5 h-full ${item.priority.includes("CAO") ? "bg-red-500" : item.priority.includes("TRUNG") ? "bg-amber-500" : "bg-blue-500"}`}
                      className={`absolute top-0 left-0 w-1.5 h-full ${item.priority >= 90 ? "bg-red-500" : item.priority < 90 && item.priority >= 70 ? "bg-amber-500" : "bg-blue-500"}`}
                    ></div>
                    <div className="flex gap-4 ml-1">
                      <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-lg shrink-0 border border-slate-200">
                        {item.icon || "💡"}
                      </div>
                      <div className="flex-1">
                        <div className="flex justify-between items-start mb-1.5">
                          <h4 className="font-extrabold text-[15px] text-slate-900 leading-tight">
                            {item.title}
                          </h4>
                        </div>
                        <p className="text-sm font-medium text-slate-600 leading-relaxed mb-3">
                          {item.reason}
                        </p>

                        {/* Bổ sung voice_txt (trích xuất insight) từ data thật */}
                        {item.voice_txt && (
                          <div className="flex items-start gap-2 text-xs italic bg-slate-50 p-3 rounded-lg border border-slate-200 mb-4">
                            <Sparkles
                              size={14}
                              className="mt-0.5 flex-shrink-0 text-purple-600"
                            />
                            <span className="text-slate-700 font-medium">
                              "{item.voice_txt}"
                            </span>
                          </div>
                        )}

                        {item.action_text && (
                          <button className="w-full py-2.5 p-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-bold transition-all shadow-md flex items-center justify-center gap-2">
                            <span>{item.action_text}</span> 
                            <ArrowRight size={50} />{" "}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-slate-500 py-10">
                  <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-3">
                    <Sparkles size={24} className="text-slate-400" />
                  </div>
                  <span className="font-semibold">Chưa có đề xuất mới</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {revenueData && (
        <div className="space-y-6 pt-6">
          <h2 className="text-2xl font-extrabold text-slate-900 flex items-center gap-2">
            <BarChart2 size={28} className="text-blue-600" /> Tổng Quan Kinh
            Doanh{" "}
            <span className="text-lg font-bold text-slate-500 ml-2">
              ({revenueData.month})
            </span>
          </h2>

          {/* Highlight Banner Khung Giờ */}
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl p-6 shadow-sm border border-amber-200 flex flex-col md:flex-row items-start md:items-center justify-between gap-5 relative overflow-hidden">
            <div className="absolute right-0 top-0 opacity-[0.03]">
              <Clock size={150} />
            </div>
            <div className="flex items-center gap-4 relative z-10">
              <div className="p-3.5 bg-white text-amber-600 rounded-xl shadow-sm border border-amber-100">
                <Clock size={28} />
              </div>
              <div>
                <h3 className="font-extrabold text-xl text-amber-900 tracking-tight">
                  Phân tích Lưu lượng Khách
                </h3>
                <p className="text-sm font-bold text-amber-800/70 mt-0.5">
                  Khung thời gian vàng để tối ưu nhân sự
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-3 relative z-10 w-full md:w-auto">
              <div className="px-5 py-3 bg-white rounded-xl shadow-sm border border-amber-100 flex-1 md:flex-none flex flex-col items-center justify-center">
                <span className="text-xs font-black text-slate-500 uppercase tracking-widest mb-1">
                  Cao điểm
                </span>
                <span className="text-xl font-black text-emerald-700">
                  {revenueData.analytics.peak_hour}
                </span>
              </div>
              <div className="px-5 py-3 bg-white rounded-xl shadow-sm border border-slate-200 flex-1 md:flex-none flex flex-col items-center justify-center">
                <span className="text-xs font-black text-slate-500 uppercase tracking-widest mb-1">
                  Thấp điểm
                </span>
                <span className="text-xl font-black text-slate-700">
                  {revenueData.analytics.low_hour}
                </span>
              </div>
              <div className="px-5 py-3 bg-white rounded-xl shadow-sm border border-amber-200 flex-1 md:flex-none flex flex-col items-center justify-center">
                <span className="text-xs font-black text-slate-500 uppercase tracking-widest mb-1">
                  Đông nhất
                </span>
                <span className="text-xl font-black text-amber-700">
                  {revenueData.analytics.best_day_of_week.day}
                </span>
              </div>
            </div>
          </div>

          {/* KPI Cards (3 cột) */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Cột 1: Tổng doanh thu */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200 flex flex-col justify-between">
              <p className="text-xs font-black text-slate-500 uppercase tracking-widest mb-3 flex items-center gap-2">
                <CalendarDays size={18} className="text-blue-600" /> Tổng Doanh Thu Tháng
              </p>
              <div className="flex items-end gap-3">
                <h3 className="text-4xl font-black text-green-500 tabular-nums tracking-tight">
                  {formatVND1000(revenueData.summary.revenue_month)}
                </h3>
              </div>
              <p className="text-sm font-semibold text-slate-500 mt-5 pt-4 border-t border-slate-100">
                Lũy kế năm:{" "}
                <span className="text-slate-800 font-bold">
                  {formatVND1000(revenueData.summary.revenue_year)}
                </span>
              </p>
            </div>

            {/* Cột 2: Tổng giao dịch */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200 flex flex-col justify-between">
              <p className="text-xs font-black text-slate-500 uppercase tracking-widest mb-3 flex items-center gap-2">
                <Activity size={18} className="text-blue-600" /> Tổng Giao Dịch
                Tháng
              </p>
              <div className="flex items-end gap-3">
                <h3 className="text-4xl font-black text-slate-900 tabular-nums tracking-tight">
                  {revenueData.summary.tx_count_month}{" "}
                  <span className="text-xl font-bold text-slate-600">đơn</span>
                </h3>
              </div>
              <p className="text-sm font-semibold text-slate-500 mt-5 pt-4 border-t border-slate-100">
                Lũy kế năm:{" "}
                <span className="text-slate-800 font-bold">
                  {revenueData.summary.tx_count_year || 0} đơn
                </span>
              </p>
            </div>

            {/* Cột 3: Giá trị TB/Đơn */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200 flex flex-col justify-between">
              <p className="text-xs font-black text-slate-500 uppercase tracking-widest mb-3 flex items-center gap-2">
                <Banknote size={18} className="text-green-600" /> Giá trị TB/Đơn
              </p>
              <div className="flex items-end gap-3">
                <h3 className="text-4xl font-black text-slate-900 tabular-nums tracking-tight">
                  {/* Sửa lại công thức tính AOV cho chuẩn xác thay vì gọi nhầm revenue_year như code cũ */}
                  {revenueData.summary.tx_count_month > 0
                    ? formatVND1000(
                        revenueData.summary.revenue_month /
                          revenueData.summary.tx_count_month,
                      )
                    : "0 đ"}
                </h3>
              </div>
              <p className="text-sm font-semibold text-slate-500 mt-5 pt-4 border-t border-slate-100">
                Trung bình trên tổng hóa đơn tháng
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
